// @flow strict

import * as React from 'react';
import axios from 'axios';
import PageContentBlock from '../../internal/PageContentBlock';
import appendScriptOnce from '../../internal/util/appendScriptOnce';
import WistiaVideo from '../../internal/WistiaVideo';

const blockVariantMap = {
  Default: 'Featured',
  Low: 'Featured',
  Medium: 'Default',
  High: 'DefaultContrast',
  Dark: 'Dark',
};

// Convert duration from seconds to ISO 8601 format (PT[minutes]M[seconds]S)
const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `PT${minutes}M${remainingSeconds}S`;
};

type PropTypes = {|
  background: 'Default' | 'Low' | 'Medium' | 'High' | 'Dark',
  title?: string,
  intro?: string,
  wistiaId: string,
  aspectRatio: '16:9' | '4:3',
|};

const Video = (props: PropTypes) => {
  const [videoMetadata, setVideoMetadata] = React.useState<{
    title: string,
    thumbnailUrl: string,
    duration: string,
  } | null>(null);

  React.useEffect(() => {
    appendScriptOnce({
      src: 'https://fast.wistia.com/assets/external/E-v1.js',
      id: 'wistiaScript',
    });
    appendScriptOnce({
      src: `https://fast.wistia.com/embed/medias/${props.wistiaId}.jsonp`,
      id: `wistia-${props.wistiaId}`,
    });

    // Fetch video metadata from Wistia
    axios
      .get(
        `https://fast.wistia.com/oembed?url=https://home.wistia.com/medias/${
          props.wistiaId
        }&format=json`,
      )
      .then(response => response.data)
      .then(data => {
        setVideoMetadata({
          title: data.title || 'Untitled Video',
          thumbnailUrl: data.thumbnail_url || '',
          duration: formatDuration(data.duration || 0),
        });
      })
      .catch(error => console.error('Failed to fetch Wistia metadata:', error));
  }, [props.wistiaId]);

  // Generate structured data only if metadata is loaded
  const structuredData = videoMetadata
    ? {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        name: videoMetadata.title,
        description: props.intro || 'No description available.',
        thumbnailUrl: videoMetadata.thumbnailUrl,
        uploadDate: new Date().toISOString(), // No upload date from Wistia API, so using current time
        duration: videoMetadata.duration,
      }
    : null;

  return (
    <PageContentBlock
      title={props.title}
      intro={props.intro}
      layout="Thin"
      background={blockVariantMap[props.background]}
    >
      <div>
        <WistiaVideo aspectRatio={props.aspectRatio} wistiaId={props.wistiaId} />
      </div>

      {/* Inject structured data only if metadata is available */}
      {structuredData && (
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      )}
    </PageContentBlock>
  );
};

Video.defaultProps = {
  aspectRatio: '16:9',
  background: 'Default',
};

export default Video;
